export type EmailTemplateType = 'ORDER_CONFIRMATION' | 'INVOICE' | 'STATUS_UPDATE';

export interface EmailTemplate {
  id: number;
  name: string;
  type: EmailTemplateType;
  subject: string;
  body: string;
  attachPdf: boolean;
  active: boolean;
  createdOn?: string;
  modifiedOn?: string;
}

export interface EmailTemplateFormData {
  name: string;
  type: EmailTemplateType;
  subject: string;
  body: string;
  attachPdf: boolean;
  active: boolean;
}

export interface EmailTemplateRequest {
  name: string;
  type: EmailTemplateType;
  subject: string;
  body: string;
  attachPdf: boolean;
  active: boolean;
}

export const emptyEmailTemplateFormData: EmailTemplateFormData = {
  name: '',
  type: 'ORDER_CONFIRMATION',
  subject: '',
  body: '',
  attachPdf: true,
  active: true,
};

export const mapEmailTemplateToFormData = (template?: EmailTemplate): EmailTemplateFormData => ({
  name: template?.name ?? '',
  type: template?.type ?? 'ORDER_CONFIRMATION',
  subject: template?.subject ?? '',
  body: template?.body ?? '',
  attachPdf: template?.attachPdf ?? true,
  active: template?.active ?? true,
});

/** Placeholder tokens available per template type, must match EmailPlaceholderRenderer on the backend. */
export const EMAIL_TEMPLATE_PLACEHOLDERS: Record<EmailTemplateType, string[]> = {
  ORDER_CONFIRMATION: [
    'customerName',
    'customerEmail',
    'customerPhone',
    'companyName',
    'reservationNumber',
    'vehicle',
    'licensePlate',
    'startDate',
    'endDate',
    'pickupTime',
    'returnTime',
    'pickupLocation',
    'returnLocation',
    'totalDays',
    'totalAmount',
    'currency',
  ],
  INVOICE: [
    'customerName',
    'customerEmail',
    'customerPhone',
    'companyName',
    'invoiceNumber',
    'invoiceDate',
    'dueDate',
    'vehicle',
    'startDate',
    'endDate',
    'pickupLocation',
    'returnLocation',
    'totalAmount',
    'balanceDue',
    'amountPaid',
    'currency',
  ],
  STATUS_UPDATE: [
    'customerName',
    'customerEmail',
    'customerPhone',
    'companyName',
    'reservationNumber',
    'vehicle',
    'licensePlate',
    'startDate',
    'endDate',
    'pickupTime',
    'returnTime',
    'pickupLocation',
    'returnLocation',
    'totalDays',
    'totalAmount',
    'currency',
    'previousStatus',
    'newStatus',
    'statusChangeReason',
  ],
};

export interface EmailPreview {
  templateId: number;
  recipientEmail: string;
  subject: string;
  body: string;
  attachPdf: boolean;
  attachmentFileName: string;
}

export interface EmailSendRequest {
  templateId: number | null;
  recipientEmail: string;
  subject: string;
  body: string;
  attachPdf: boolean;
}

export interface EmailSendResult {
  success: boolean;
  message: string;
}

/**
 * All backend Reservation.ReservationStatus values, in display order. Deliberately not sourced from
 * frontend/src/types/reservation.ts's ReservationStatus enum, which is missing OVERDUE.
 */
export const RESERVATION_STATUS_VALUES = [
  'PENDING',
  'CONFIRMED',
  'IN_PROGRESS',
  'COMPLETED',
  'CANCELLED',
  'NO_SHOW',
  'OVERDUE',
] as const;

export type ReservationStatusValue = (typeof RESERVATION_STATUS_VALUES)[number];

export interface ReservationStatusEmailRule {
  id: number | null;
  status: ReservationStatusValue;
  templateId: number | null;
  enabled: boolean;
  modifiedOn?: string;
}

export interface ReservationStatusEmailRuleRequest {
  status: ReservationStatusValue;
  templateId: number | null;
  enabled: boolean;
}
